import { api } from "../api";
import { iconoLapiz, iconoMas, iconoLlave } from "../iconos";
import type { Cliente, Lista, AccesoWeb, AccesoWebCreado } from "../../lib/types";

type ClienteFila = Cliente & { lista_nombre: string };

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

export async function renderClientes(contenedor: HTMLElement): Promise<void> {
  let listas: Lista[] = [];
  let todos: ClienteFila[] = [];

  try {
    const meta = await api.get<{ listas: Lista[] }>("/api/meta");
    listas = meta.listas;
  } catch {
    /* ignore */
  }
  const opcLista = (sel?: number) =>
    listas
      .map(
        (l) => `<option value="${l.id}"${l.id === sel ? " selected" : ""}>${esc(l.nombre)}${l.descripcion ? " — " + esc(l.descripcion) : ""}</option>`,
      )
      .join("");

  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina">Clientes</h1>
      <p class="subtitulo">Alta y edición de clientes mayoristas.</p>
    </div>

    <details class="tarjeta mb-4">
      <summary class="flex cursor-pointer list-none items-center gap-2 text-sm font-bold uppercase tracking-wide text-masa-600">
        <span class="text-masa-500">${iconoMas}</span> Nuevo cliente
      </summary>
      <form id="f" class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label><span class="etiqueta">Código</span><input id="codigo" class="campo" placeholder="CU0100" /></label>
        <label><span class="etiqueta">Nombre</span><input id="nombre" class="campo" placeholder="Nombre del comercio" /></label>
        <label><span class="etiqueta">Lista de precios</span><select id="lista_id" class="campo">${opcLista()}</select></label>
        <label><span class="etiqueta">Contacto</span><input id="contacto" class="campo" placeholder="Persona de contacto" /></label>
        <label><span class="etiqueta">Dirección</span><input id="direccion" class="campo" /></label>
        <label><span class="etiqueta">Teléfono</span><input id="telefono" class="campo" /></label>
        <div class="flex items-center gap-2 sm:col-span-2">
          <button class="btn-primario">${iconoMas} Agregar</button>
          <span id="err" class="text-sm text-red-600"></span>
        </div>
      </form>
    </details>

    <div class="tarjeta overflow-x-auto p-0">
      <table class="tabla">
        <thead><tr>
          <th>Código</th><th>Nombre</th><th>Lista</th><th>Contacto</th><th>Teléfono</th><th class="text-right">Acciones</th>
        </tr></thead>
        <tbody id="tbody"></tbody>
      </table>
      <div id="pie" class="border-t border-crema-200 px-3 py-2 text-sm text-masa-500"></div>
    </div>

    <dialog id="dlg"></dialog>`;

  const tbody = contenedor.querySelector("#tbody") as HTMLElement;
  const pie = contenedor.querySelector("#pie") as HTMLElement;
  const err = contenedor.querySelector("#err") as HTMLElement;
  const dlg = contenedor.querySelector("#dlg") as HTMLDialogElement;

  function pintar(): void {
    tbody.innerHTML = todos
      .map(
        (c) => `<tr>
          <td class="font-medium">${esc(c.codigo)}</td>
          <td>${esc(c.nombre)}</td>
          <td>${esc(c.lista_nombre)}</td>
          <td>${esc(c.contacto ?? "")}</td>
          <td>${esc(c.telefono ?? c.celular ?? "")}</td>
          <td class="text-right">
            <button class="chip-accion" data-acceso="${c.id}" title="Acceso web del local">${iconoLlave}</button>
            <button class="chip-accion" data-editar="${c.id}" title="Editar">${iconoLapiz}</button>
          </td>
        </tr>`,
      )
      .join("");
    pie.textContent = `Mostrando ${todos.length} clientes`;
  }

  async function cargar(): Promise<void> {
    tbody.innerHTML = `<tr><td colspan="6" class="py-6 text-center text-masa-400">Cargando...</td></tr>`;
    todos = await api.get<ClienteFila[]>("/api/clientes?activo=1");
    pintar();
  }

  function avisoModal(mensaje: string): void {
    dlg.innerHTML = `
      <div class="tarjeta w-[24rem] max-w-full border-0 shadow-none">
        <h2 class="mb-2 text-lg font-bold text-red-700">Código duplicado</h2>
        <p class="text-sm text-masa-700">${mensaje}</p>
        <div class="mt-4 flex justify-end"><button id="ok" class="btn-primario">Entendido</button></div>
      </div>`;
    dlg.querySelector("#ok")?.addEventListener("click", () => dlg.close());
    dlg.showModal();
  }

  const form = contenedor.querySelector("#f") as HTMLFormElement;
  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    err.textContent = "";
    const val = (id: string) => (contenedor.querySelector("#" + id) as HTMLInputElement).value.trim();
    if (!val("codigo") || !val("nombre")) {
      err.textContent = "Código y nombre son obligatorios";
      return;
    }
    const dup = todos.find((c) => c.codigo.toLowerCase() === val("codigo").toLowerCase());
    if (dup) {
      avisoModal(`El código ${val("codigo")} ya existe (cliente: ${esc(dup.nombre)}).`);
      return;
    }
    try {
      await api.post("/api/clientes", {
        codigo: val("codigo"), nombre: val("nombre"),
        lista_id: Number((contenedor.querySelector("#lista_id") as HTMLSelectElement).value),
        contacto: val("contacto") || null, direccion: val("direccion") || null,
        telefono: val("telefono") || null,
      });
      form.reset();
      await cargar();
    } catch (e) {
      err.textContent = e instanceof Error ? e.message : "Error";
    }
  });

  tbody.addEventListener("click", (ev) => {
    const objetivo = ev.target as HTMLElement;
    const btnAcceso = objetivo.closest("[data-acceso]");
    if (btnAcceso) {
      const c = todos.find((x) => x.id === Number(btnAcceso.getAttribute("data-acceso")));
      if (c) void abrirAcceso(c);
      return;
    }
    const btn = objetivo.closest("[data-editar]");
    if (!btn) return;
    const c = todos.find((x) => x.id === Number(btn.getAttribute("data-editar")));
    if (c) abrirEdicion(c);
  });

  // --------------------------------------------------------------- acceso web
  // Da de alta al encargado del local: se carga su correo de Google y entra
  // desde el celular con "Entrar con Google". No hay clave que pasarle.
  async function abrirAcceso(c: ClienteFila): Promise<void> {
    // Puede haber mas de un acceso: el propio del local y alguien que maneja
    // varios. Misma regla que accesoPropio() del backend — el que se llama como
    // el codigo, o el unico vinculado si lo renombraron (lo0001 -> local1).
    let accesos: AccesoWeb[] = [];
    try {
      const r = await api.get<{ accesos: AccesoWeb[] }>(`/api/clientes/${c.id}/acceso`);
      accesos = r.accesos ?? [];
    } catch {
      /* si falla, se muestra como si no tuviera */
    }
    const derivado = c.codigo.trim().toLowerCase();
    const propios = accesos.filter((a) => a.locales === 1);
    const actual =
      accesos.find((a) => a.usuario === derivado) ?? (propios.length === 1 ? propios[0] : null);
    const otros = accesos.filter((a) => a.id !== actual?.id);

    const estado = !actual
      ? `<p class="text-sm text-masa-600">Este local todavia <b>no tiene acceso web</b>. Escribí el correo de Google del encargado: va a entrar con esa cuenta, sin clave.</p>`
      : !actual.activo
        ? `<p class="text-sm text-amber-700">El acceso de este local está <b>dado de baja</b>. Si guardás un correo, se reactiva.</p>`
        : actual.email
          ? `<p class="text-sm text-masa-600">El encargado entra con <b>Entrar con Google</b>, usando esta cuenta. Para pasárselo a otra persona, cambiá el correo.</p>`
          : `<p class="text-sm text-amber-700">Este local <b>no puede entrar</b>: no tiene ningún correo cargado. Poné el del encargado.</p>`;

    dlg.innerHTML = `
      <div class="tarjeta w-[28rem] max-w-full border-0 shadow-none">
        <h2 class="mb-1 text-lg font-bold">Acceso web</h2>
        <p class="mb-4 text-sm text-masa-500">${esc(c.nombre)} · ${esc(c.codigo)}</p>
        <div id="a_estado">${estado}</div>
        ${
          otros.length > 0
            ? `<p class="mt-2 rounded-lg bg-crema-100 p-2 text-sm text-masa-600">
                Ademas pueden pedir por este local: <b>${otros.map((o) => esc(o.usuario)).join(", ")}</b>.
                Esos se editan desde la pantalla <b>Usuarios</b>.
              </p>`
            : ""
        }
        <label class="mt-3 block"><span class="etiqueta">Correo de Google del encargado</span>
          <input id="a_email" type="email" class="campo" value="${esc(actual?.email ?? "")}" placeholder="ej: encargada@gmail.com"
                 autocapitalize="none" autocorrect="off" spellcheck="false" /></label>
        <div id="a_credenciales" class="mt-3 hidden rounded-xl border border-green-200 bg-green-50 p-3"></div>
        <p id="a_err" class="mt-2 text-sm text-red-600"></p>
        <div class="mt-4 flex flex-wrap justify-end gap-2">
          ${actual && actual.activo ? `<button id="a_baja" class="btn-peligro">Dar de baja</button>` : ""}
          <button id="a_cerrar" class="btn-sec">Cerrar</button>
          <button id="a_crear" class="btn-primario">${actual ? "Guardar correo" : "Crear acceso"}</button>
        </div>
      </div>`;

    const aErr = dlg.querySelector("#a_err") as HTMLElement;
    const aCred = dlg.querySelector("#a_credenciales") as HTMLElement;

    dlg.querySelector("#a_cerrar")?.addEventListener("click", () => dlg.close());

    dlg.querySelector("#a_crear")?.addEventListener("click", async () => {
      aErr.textContent = "";
      const email = (dlg.querySelector("#a_email") as HTMLInputElement).value.trim();
      if (!email.includes("@")) {
        aErr.textContent = "Escribí el correo de Google del encargado";
        return;
      }
      try {
        const r = await api.post<AccesoWebCreado>(`/api/clientes/${c.id}/acceso`, { email });
        aCred.classList.remove("hidden");
        aCred.innerHTML = `
          <p class="text-sm font-bold text-green-800">Listo: ${esc(r.email)} ya puede entrar.</p>
          <p class="mt-1 text-sm text-green-800">Decile que abra la misma dirección web y toque <b>Entrar con Google</b> con esa cuenta. No hay clave.</p>`;
        (dlg.querySelector("#a_estado") as HTMLElement).innerHTML = "";
      } catch (e) {
        aErr.textContent = e instanceof Error ? e.message : "Error";
      }
    });

    dlg.querySelector("#a_baja")?.addEventListener("click", async () => {
      aErr.textContent = "";
      try {
        await api.del(`/api/clientes/${c.id}/acceso`);
        dlg.close();
      } catch (e) {
        aErr.textContent = e instanceof Error ? e.message : "Error";
      }
    });

    dlg.showModal();
  }

  function abrirEdicion(c: ClienteFila): void {
    const campo = (id: string, lbl: string, valor: string | null, col = "") =>
      `<label class="${col}"><span class="etiqueta">${lbl}</span><input id="${id}" class="campo" value="${esc(valor ?? "")}" /></label>`;
    dlg.innerHTML = `
      <form method="dialog" class="tarjeta w-[30rem] max-w-full border-0 shadow-none">
        <h2 class="mb-1 text-lg font-bold">Editar cliente</h2>
        <p class="mb-4 text-sm text-masa-500">Código #${esc(c.codigo)}</p>
        <div class="grid grid-cols-2 gap-3">
          ${campo("e_nombre", "Nombre", c.nombre, "col-span-2")}
          <label class="col-span-2"><span class="etiqueta">Lista de precios</span>
            <select id="e_lista" class="campo">${opcLista(c.lista_id)}</select></label>
          ${campo("e_contacto", "Contacto", c.contacto)}
          ${campo("e_direccion", "Dirección", c.direccion)}
          ${campo("e_telefono", "Teléfono", c.telefono)}
          ${campo("e_celular", "Celular", c.celular)}
          ${campo("e_email", "Email", c.email, "col-span-2")}
          ${campo("e_notas", "Notas", c.notas, "col-span-2")}
          <label class="col-span-2 flex items-center gap-2 pt-1">
            <input id="e_activo" type="checkbox" ${c.activo ? "checked" : ""} class="h-4 w-4 accent-masa-600" />
            <span class="text-sm">Activo</span></label>
        </div>
        <p id="e_err" class="mt-2 text-sm text-red-600"></p>
        <div class="mt-4 flex justify-end gap-2">
          <button value="cancel" class="btn-sec">Cancelar</button>
          <button id="e_guardar" value="ok" class="btn-primario">Guardar</button>
        </div>
      </form>`;
    const eErr = dlg.querySelector("#e_err") as HTMLElement;
    dlg.querySelector("#e_guardar")?.addEventListener("click", async (ev) => {
      ev.preventDefault();
      eErr.textContent = "";
      const val = (id: string) => (dlg.querySelector("#" + id) as HTMLInputElement).value.trim();
      try {
        await api.put(`/api/clientes/${c.id}`, {
          nombre: val("e_nombre"),
          lista_id: Number((dlg.querySelector("#e_lista") as HTMLSelectElement).value),
          contacto: val("e_contacto") || null, direccion: val("e_direccion") || null,
          telefono: val("e_telefono") || null, celular: val("e_celular") || null,
          email: val("e_email") || null, notas: val("e_notas") || null,
          activo: (dlg.querySelector("#e_activo") as HTMLInputElement).checked ? 1 : 0,
        });
        dlg.close();
        await cargar();
      } catch (e) {
        eErr.textContent = e instanceof Error ? e.message : "Error";
      }
    });
    dlg.showModal();
  }

  await cargar();
}
